import { InvoiceLineItem } from './types';

export type DiscountableLine = Pick<
  InvoiceLineItem,
  'price' | 'quantity' | 'discountType' | 'discountAmount'
>;

// Quantity is a decimal with 2 fractional digits on the server (GraphQL Float).
// The UI must never enter or display more precision than that.
export const QUANTITY_DECIMALS = 2;

// Display formatter — always renders quantity with 2 decimal places so read-only
// values match the precision the server stores and the input's step allows.
export const quantityFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: QUANTITY_DECIMALS,
  maximumFractionDigits: QUANTITY_DECIMALS,
});

// Round to the server's 2-decimal precision. Guards against float drift from
// text input (e.g. "1.005") and returns NaN unchanged so callers can validate.
export function roundQuantity(value: number): number {
  if (!Number.isFinite(value)) return value;
  return Math.round(value * 100) / 100;
}

// quantity is stored ≥ 1 on the server; be defensive against a missing/0 value in cached data.
export function lineQuantity(line: DiscountableLine): number {
  return line.quantity && line.quantity > 0 ? line.quantity : 1;
}

// Gross price for a line, scaled by quantity. `price` is per-unit.
export function invoiceLineGrossAmount(line: DiscountableLine): number {
  return line.price * lineQuantity(line);
}

// Net charge for a single invoice line: quantity-scaled gross minus its stamped discount.
// Mirrors the backend's lineItemNetAmount — display-only; authoritative billing math lives on the backend.
// Percent discount scales with quantity naturally; amount-off is a flat per-line figure and does not.
export function invoiceLineNetAmount(line: DiscountableLine): number {
  const gross = invoiceLineGrossAmount(line);
  switch (line.discountType) {
    case 'PERCENT_OFF':
      return gross * (1 - line.discountAmount / 100);
    case 'AMOUNT_OFF':
      return Math.max(0, gross - line.discountAmount);
    default:
      return gross;
  }
}

// Total amount for an invoice, summed in integer cents to avoid float drift across lines.
export function invoiceAmount(lineItems: DiscountableLine[]): number {
  const cents = lineItems.reduce(
    (sum, line) => sum + Math.round(invoiceLineNetAmount(line) * 100),
    0,
  );
  return cents / 100;
}

// Gross (pre-discount) total — sum of the quantity-scaled line prices. Used to show the "was $X" cue.
export function invoiceGrossAmount(lineItems: DiscountableLine[]): number {
  const cents = lineItems.reduce(
    (sum, line) => sum + Math.round(invoiceLineGrossAmount(line) * 100),
    0,
  );
  return cents / 100;
}
